import React, { useEffect, useState } from "react";
import { UserPlus, Copy, Trash2, Mail, Users, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "../lib/api";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import Panda from "./Panda";

// Parent-side: invite grandparents/mentors to follow children (read-only), manage pending invites & followers.
const InviteManager = ({ children }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState(() => children.map((c) => c.id));
  const [role, setRole] = useState("follower");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ pending: [], followers: [] });
  const [lastLink, setLastLink] = useState(null);

  const load = () => api.get("/invites").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { setSelected((s) => (s.length ? s.filter((id) => children.some((c) => c.id === id)) : children.map((c) => c.id))); }, [children]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch {
      window.prompt("Copy this invite link:", link);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (role === "follower" && !selected.length) { toast.error("Choose at least one child"); return; }
    setBusy(true);
    try {
      const r = await api.post("/invites", { email, child_ids: role === "parent" ? [] : selected, message: message || null, role });
      setLastLink(r.data.accept_url);
      if (r.data.email_sent) toast.success(`Invitation emailed to ${email}`);
      else toast("Invite created - email couldn't be delivered", { description: "Share the link below directly instead." });
      setEmail(""); setMessage("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not create invite"));
    } finally { setBusy(false); }
  };

  const revoke = async (id) => { await api.delete(`/invites/${id}`); toast("Invite revoked"); load(); };
  const [resending, setResending] = useState(null);
  const resend = async (p) => {
    setResending(p.id);
    try {
      const r = await api.post(`/invites/${p.id}/resend`);
      setLastLink(r.data.accept_url);
      if (r.data.email_sent) toast.success(`Reminder sent to ${p.email}`, { description: "A fresh link was emailed; the old one no longer works." });
      else toast("Reminder created - email couldn't be delivered", { description: "Share the new link below directly instead." });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not resend invite"));
    } finally { setResending(null); }
  };
  const removeFollower = async (f) => {
    if (!window.confirm(`Remove ${f.name} as a follower?`)) return;
    await api.delete(`/followers/${f.id}`); toast(`${f.name} removed`); load();
  };

  return (
    <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8" data-testid="invite-manager">
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl bg-[#e8f8ef] text-[#2e8b57] flex items-center justify-center flex-shrink-0"><Users /></span>
        <div>
          <h3 className="font-bold text-[#0c4a6e] text-xl" style={{ fontFamily: "Literata, serif" }}>Invite family to join</h3>
          <p className="text-gray-600 text-sm mt-1">Grandparents and mentors get a read-only view and can send cheers. A second parent gets full access - same children, same progress, both logins.</p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="invite-role">
        {[
          ["follower", "Follow (read-only)", "Sees lessons, quiz scores and certificates. Can send cheers.", "#63c79a"],
          ["parent", "Co-parent (full access)", "Adds and edits children, runs lessons, manages the family.", "#7d5fb2"],
        ].map(([id, title, desc, color]) => (
          <button key={id} type="button" onClick={() => setRole(id)} className={`text-left rounded-2xl border-2 p-4 transition-colors ${role === id ? "bg-white shadow-md" : "border-[#e9f9fd] hover:border-[#c9ecf5]"}`} style={role === id ? { borderColor: color } : {}} data-testid={`invite-role-${id}`}>
            <p className="font-bold text-[#0c4a6e] flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: color }} />{title}</p>
            <p className="text-xs text-gray-600 mt-1">{desc}</p>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-6 grid md:grid-cols-[1fr_auto] gap-4 items-start" data-testid="invite-form">
        <div className="space-y-3">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="grandma@example.com" className="h-11 rounded-full px-5 border-2 border-[#c9ecf5] focus-visible:ring-[#1b8bc9]" data-testid="invite-email-input" />
          <Input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} placeholder="Optional note, e.g. 'Come see what Maya is learning!'" className="h-11 rounded-full px-5 border-2 border-[#e9f9fd] focus-visible:ring-[#1b8bc9]" data-testid="invite-message-input" />
          <div className="flex flex-wrap gap-3">
            {role === "parent" ? (
              <p className="text-xs text-gray-500 italic">Co-parents automatically get access to every child in the family.</p>
            ) : children.map((c) => (
              <label key={c.id} className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border-2 cursor-pointer transition-colors ${selected.includes(c.id) ? "border-[#63c79a] bg-[#e8f8ef]" : "border-[#e9f9fd]"}`}>
                <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} className="ml-1 data-[state=checked]:bg-[#2e8b57] data-[state=checked]:border-[#2e8b57]" data-testid={`invite-child-${c.id}`} />
                <Panda size={24} accessory={c.panda.accessory} scarf={c.panda.scarf} />
                <span className="text-sm font-bold text-[#0c4a6e]">{c.panda.name}</span>
              </label>
            ))}
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-pill bg-[#63c79a] hover:bg-[#4fb98a] text-white inline-flex items-center gap-2 disabled:opacity-60 h-11" data-testid="invite-submit"><UserPlus size={18} /> {busy ? "Sending..." : "Send invite"}</button>
      </form>

      {lastLink && (
        <div className="mt-4 rounded-2xl bg-[#e9f9fd] p-4 text-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid="invite-link-box">
          <p className="flex-1 text-[#0c4a6e]"><span className="font-bold">Invite link</span> (also emailed): <span className="font-mono text-xs break-all">{lastLink}</span></p>
          <button onClick={() => copy(lastLink)} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white text-sm inline-flex items-center gap-1" data-testid="copy-invite-link"><Copy size={14} /> Copy</button>
        </div>
      )}

      {(data.pending.length > 0 || data.followers.length > 0) && (
        <div className="mt-6 grid md:grid-cols-2 gap-6 border-t border-[#e9f9fd] pt-6">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Pending invites</p>
            {data.pending.length === 0 ? <p className="text-sm text-gray-400 italic">None</p> : data.pending.map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`pending-invite-${p.id}`}>
                <Mail size={16} className="text-[#f7931e] flex-shrink-0" />
                <div className="flex-1 min-w-0"><p className="font-semibold text-[#0c4a6e] truncate">{p.email} {p.role === "parent" && <span className="ml-1 text-[10px] uppercase font-bold text-[#7d5fb2] bg-[#f3e8ff] rounded-full px-2 py-0.5">co-parent</span>}</p><p className="text-xs text-gray-500">{p.child_names.join(", ")}{p.resend_count ? ` · reminded ${p.resend_count}x` : ""}</p></div>
                <button onClick={() => resend(p)} disabled={resending === p.id} className="inline-flex items-center gap-1 text-xs font-bold text-[#1b8bc9] bg-[#e9f9fd] hover:bg-[#1b8bc9] hover:text-white rounded-full px-3 py-1 transition-colors disabled:opacity-60" aria-label="Resend invite" data-testid={`resend-invite-${p.id}`}><RefreshCw size={12} className={resending === p.id ? "animate-spin" : ""} /> {resending === p.id ? "Sending" : "Remind"}</button>
                <button onClick={() => revoke(p.id)} className="text-gray-400 hover:text-[#e5356f]" aria-label="Revoke" data-testid={`revoke-invite-${p.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">In your family</p>
            {data.followers.length === 0 ? <p className="text-sm text-gray-400 italic">No followers yet</p> : data.followers.map((f) => (
              <div key={f.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`follower-${f.id}`}>
                <span className="w-8 h-8 rounded-full bg-[#7d5fb2] text-white flex items-center justify-center font-bold flex-shrink-0">{f.name.charAt(0).toUpperCase()}</span>
                <div className="flex-1 min-w-0"><p className="font-semibold text-[#0c4a6e] truncate">{f.name} <span className="text-gray-400 font-normal">{f.email}</span> <span className={`ml-1 text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${f.role === "parent" ? "text-[#7d5fb2] bg-[#f3e8ff]" : "text-[#0d4d33] bg-[#e8f8ef]"}`} data-testid={`follower-role-${f.id}`}>{f.role === "parent" ? "co-parent" : "follower"}</span></p><p className="text-xs text-gray-500 inline-flex items-center gap-1"><Check size={12} className="text-[#2e8b57]" /> {f.child_names.join(", ")}</p></div>
                <button onClick={() => removeFollower(f)} className="text-gray-400 hover:text-[#e5356f]" aria-label="Remove follower" data-testid={`remove-follower-${f.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteManager;
